import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Maximize2, Minus, Move, Plus, RotateCcw, X } from 'lucide-react';
import { patchKitMarketingCopy } from './buildKitMarketingCopy';
import '../fabricTemplates/fabricTemplates.css';

const PLACEHOLDER = 'https://placehold.co/600x800/e8ddd4/5c564e?text=Image';
const MIN_SCALE = 1;
const MAX_SCALE = 3;
const DEFAULT_TRANSFORM = { scale: 1, x: 50, y: 50 };
const MIN_FABRIC_SIZE = 0.55;
const MAX_FABRIC_SIZE = 2.2;
const DEFAULT_FABRIC_SIZE = 1;
/** Overlay top-left as % of the poster content area */
const DEFAULT_FABRIC_POS = { x: 3.5, y: 72 };
const DEFAULT_DETAILS_POS = { x: 52, y: 62 };
/** null = size to content; otherwise width as % of stage */
const MIN_DETAILS_WIDTH_PCT = 16;
const MAX_DETAILS_WIDTH_PCT = 90;
const DETAIL_FONT_STEPS = [0.85, 1, 1.15, 1.3, 1.5, 1.75];
const DETAIL_COLOR_PRESETS = ['#ffffff', '#0b1220', '#1a2744', '#6b2c3e', '#0f766e'];

function FlyerShell({ children, className = '' }) {
  return (
    <div className={`ftl-flyer ftl-flyer--portrait ftl-root ${className}`}>
      <div className="ftl-flyer-scale absolute inset-0 flex flex-col">{children}</div>
    </div>
  );
}

function clamp(n, min, max) {
  return Math.min(max, Math.max(min, n));
}

function nearestFontStep(scale) {
  let best = DETAIL_FONT_STEPS[1];
  let bestDist = Infinity;
  for (const step of DETAIL_FONT_STEPS) {
    const d = Math.abs(step - scale);
    if (d < bestDist) {
      best = step;
      bestDist = d;
    }
  }
  return best;
}

function nudgeFontScale(current, dir) {
  const idx = DETAIL_FONT_STEPS.indexOf(nearestFontStep(Number(current) || 1));
  const next = clamp(idx + dir, 0, DETAIL_FONT_STEPS.length - 1);
  return DETAIL_FONT_STEPS[next];
}

function normalizePos(pos, fallback) {
  const x = Number(pos?.x);
  const y = Number(pos?.y);
  return {
    x: Number.isFinite(x) ? clamp(x, 0, 92) : fallback.x,
    y: Number.isFinite(y) ? clamp(y, 0, 92) : fallback.y,
  };
}

/**
 * Drag an overlay by updating top-left position as % of a container.
 */
function useOverlayDrag({ enabled, pos, onPosChange, containerRef }) {
  const dragRef = useRef(null);
  const onPosChangeRef = useRef(onPosChange);
  const posRef = useRef(pos);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    onPosChangeRef.current = onPosChange;
  }, [onPosChange]);

  useEffect(() => {
    posRef.current = pos;
  }, [pos]);

  useEffect(() => {
    if (!dragging) return undefined;

    const onMove = (e) => {
      const drag = dragRef.current;
      const container = containerRef?.current;
      const change = onPosChangeRef.current;
      if (!drag || !container || !change) return;
      const rect = container.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return;
      const dxPct = ((e.clientX - drag.startX) / rect.width) * 100;
      const dyPct = ((e.clientY - drag.startY) / rect.height) * 100;
      change({
        x: clamp(drag.origX + dxPct, 0, 92),
        y: clamp(drag.origY + dyPct, 0, 92),
      });
    };

    const onUp = () => {
      dragRef.current = null;
      setDragging(false);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [dragging, containerRef]);

  const beginDrag = useCallback(
    (e) => {
      if (!enabled || !onPosChangeRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        origX: posRef.current.x,
        origY: posRef.current.y,
      };
      setDragging(true);
    },
    [enabled]
  );

  return { dragging, beginDrag };
}

/**
 * Image tile with optional zoom + pan controls for preview.
 * cover fills the frame (crops the long edge). contain letterboxes.
 * Scale > 1 zooms in from that fit.
 */
function ImageTile({
  src,
  alt,
  className = '',
  editable = false,
  transform,
  onTransformChange,
  label,
  hideCornerHandle = false,
  fit = 'contain',
}) {
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState(false);
  const frameRef = useRef(null);
  const dragRef = useRef(null);

  const t = {
    scale: transform?.scale ?? DEFAULT_TRANSFORM.scale,
    x: transform?.x ?? DEFAULT_TRANSFORM.x,
    y: transform?.y ?? DEFAULT_TRANSFORM.y,
  };
  const displaySrc = !src || failed ? PLACEHOLDER : src;
  const bgPos = `${t.x}% ${t.y}%`;
  const isCover = fit === 'cover';
  // scale 1 = the chosen fit; >1 zooms further
  const isFit = t.scale <= 1.001;

  const update = useCallback(
    (patch) => {
      if (!onTransformChange) return;
      onTransformChange({
        scale: clamp(patch.scale ?? t.scale, MIN_SCALE, MAX_SCALE),
        x: clamp(patch.x ?? t.x, 0, 100),
        y: clamp(patch.y ?? t.y, 0, 100),
      });
    },
    [onTransformChange, t.scale, t.x, t.y]
  );

  const onPointerDownPan = (e) => {
    if (!editable || !onTransformChange) return;
    if (e.target.closest('[data-resize-ui="true"]')) return;
    // Contain at scale 1 has no overflow to pan. Cover can still be repositioned.
    if (!isCover && t.scale <= 1.001) {
      setActive(true);
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setActive(true);
    const frame = frameRef.current;
    if (!frame) return;
    const rect = frame.getBoundingClientRect();
    dragRef.current = {
      mode: 'pan',
      startX: e.clientX,
      startY: e.clientY,
      origX: t.x,
      origY: t.y,
      width: rect.width,
      height: rect.height,
    };
    frame.setPointerCapture?.(e.pointerId);
  };

  const onPointerDownResize = (e) => {
    if (!editable || !onTransformChange) return;
    e.preventDefault();
    e.stopPropagation();
    setActive(true);
    dragRef.current = {
      mode: 'resize',
      startX: e.clientX,
      startY: e.clientY,
      origScale: t.scale,
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    if (drag.mode === 'pan') {
      const dx = ((e.clientX - drag.startX) / drag.width) * 100;
      const dy = ((e.clientY - drag.startY) / drag.height) * 100;
      update({
        x: drag.origX - dx,
        y: drag.origY - dy,
      });
      return;
    }
    if (drag.mode === 'resize') {
      const delta = (e.clientX - drag.startX + (e.clientY - drag.startY)) / 120;
      update({ scale: drag.origScale + delta });
    }
  };

  const onPointerUp = (e) => {
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
  };

  const onWheel = (e) => {
    if (!editable || !onTransformChange) return;
    e.preventDefault();
    e.stopPropagation();
    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    update({ scale: t.scale + delta });
    setActive(true);
  };

  useEffect(() => {
    if (!active) return undefined;
    const onDocDown = (ev) => {
      if (frameRef.current && !frameRef.current.contains(ev.target)) {
        setActive(false);
      }
    };
    document.addEventListener('pointerdown', onDocDown);
    return () => document.removeEventListener('pointerdown', onDocDown);
  }, [active]);

  return (
    <div
      ref={frameRef}
      className={`relative overflow-hidden bg-[#e8ddd4] group/tile ${editable ? 'cursor-grab active:cursor-grabbing' : ''} ${className}`}
      data-export-frame="true"
      data-image-src={src || ''}
      data-image-fit={isCover ? 'cover' : 'contain'}
      data-image-scale={String(t.scale)}
      data-image-x={String(t.x)}
      data-image-y={String(t.y)}
      style={{
        // Keep a contain fill as fallback for PDF prep; live view uses <img> object-fit
        backgroundImage: isFit ? 'none' : `url("${displaySrc}")`,
        backgroundSize: isFit ? (isCover ? 'cover' : 'contain') : `${Math.round(t.scale * 100)}%`,
        backgroundPosition: bgPos,
        backgroundRepeat: 'no-repeat',
      }}
      onPointerDown={onPointerDownPan}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      onClick={(e) => {
        if (!editable) return;
        e.stopPropagation();
        setActive(true);
      }}
    >
      <img
        src={displaySrc}
        alt={alt || ''}
        className={`absolute inset-0 w-full h-full pointer-events-none select-none ${
          isCover ? 'object-cover' : 'object-contain'
        }`}
        style={{
          objectPosition: bgPos,
          transform: t.scale > 1 ? `scale(${t.scale})` : 'none',
          transformOrigin: `${t.x}% ${t.y}%`,
        }}
        loading="eager"
        decoding="async"
        draggable={false}
        onError={() => setFailed(true)}
      />

      {editable && onTransformChange && (
        <>
          {/* Corner drag-zoom — hidden when parent provides a window resizer */}
          {!hideCornerHandle && (
            <button
              type="button"
              data-resize-ui="true"
              data-export-hide="true"
              aria-label={`Zoom ${label || alt || 'image'}`}
              title="Drag to zoom"
              className={`absolute bottom-1.5 right-1.5 z-20 w-7 h-7 rounded-md bg-white/95 text-[#1a2744] shadow border border-black/10 flex items-center justify-center transition ${
                active ? 'opacity-100' : 'opacity-0 group-hover/tile:opacity-100'
              }`}
              onPointerDown={onPointerDownResize}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              <Maximize2 size={14} />
            </button>
          )}

          {/* Zoom controls */}
          <div
            data-resize-ui="true"
            data-export-hide="true"
            className={`absolute left-1.5 bottom-1.5 z-20 flex items-center gap-1 rounded-md bg-black/55 px-1.5 py-1 backdrop-blur-sm transition ${
              hideCornerHandle ? 'right-9' : 'right-10'
            } ${active ? 'opacity-100' : 'opacity-0 group-hover/tile:opacity-100'}`}
            onPointerDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="p-0.5 text-white/90 hover:text-white"
              aria-label="Zoom out"
              onClick={(e) => {
                e.stopPropagation();
                update({ scale: t.scale - 0.15 });
              }}
            >
              <Minus size={12} />
            </button>
            <input
              type="range"
              min={MIN_SCALE}
              max={MAX_SCALE}
              step={0.05}
              value={t.scale}
              aria-label={`Zoom ${label || alt || 'image'}`}
              className="flex-1 h-1 accent-white cursor-pointer"
              onChange={(e) => update({ scale: Number(e.target.value) })}
            />
            <button
              type="button"
              className="p-0.5 text-white/90 hover:text-white"
              aria-label="Zoom in"
              onClick={(e) => {
                e.stopPropagation();
                update({ scale: t.scale + 0.15 });
              }}
            >
              <Plus size={12} />
            </button>
            <button
              type="button"
              className="p-0.5 text-white/90 hover:text-white"
              aria-label="Reset image size"
              title="Reset"
              onClick={(e) => {
                e.stopPropagation();
                onTransformChange({ ...DEFAULT_TRANSFORM });
              }}
            >
              <RotateCcw size={11} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function CornerResizeHandle({ onPointerDown, label = 'Resize' }) {
  return (
    <button
      type="button"
      data-resize-ui="true"
      data-export-hide="true"
      aria-label={label}
      title={label}
      onPointerDown={onPointerDown}
      className="absolute bottom-0 right-0 z-50 w-8 h-8 cursor-nwse-resize touch-none flex items-end justify-end p-1"
    >
      <span className="w-5 h-5 rounded-sm bg-white border-2 border-slate-800 shadow flex items-end justify-end p-[3px]">
        <svg width="10" height="10" viewBox="0 0 12 12" aria-hidden="true">
          <path d="M9 12L12 9" stroke="#0f172a" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M5 12L12 5" stroke="#0f172a" strokeWidth="1.8" strokeLinecap="round" />
          <path d="M1 12L12 1" stroke="#0f172a" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </span>
    </button>
  );
}

/**
 * Compact key/value form for adding or editing a fabric detail row.
 */
function DetailRowModal({ open, mode = 'add', initialLabel = '', initialValue = '', onClose, onSave }) {
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const keyRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const onSaveRef = useRef(onSave);
  const isEdit = mode === 'edit';

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    onSaveRef.current = onSave;
  }, [onSave]);

  useEffect(() => {
    if (!open) return undefined;
    setLabel(String(initialLabel || ''));
    setValue(String(initialValue || ''));
    const t = window.setTimeout(() => keyRef.current?.focus(), 40);
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current?.();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open]);

  if (!open || typeof document === 'undefined') return null;

  const submit = (e) => {
    e?.preventDefault?.();
    const key = String(label || '').trim();
    const val = String(value || '').trim();
    if (!key && !val) return;
    onSaveRef.current?.({
      label: (key || 'DETAIL').toUpperCase(),
      value: val,
    });
    onCloseRef.current?.();
  };

  const titleId = 'kit-detail-row-modal-title';

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/55 backdrop-blur-[2px]"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-export-hide="true"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCloseRef.current?.();
      }}
    >
      <form
        onSubmit={submit}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[20rem] rounded-xl bg-[#1e1a24] border border-white/12 shadow-2xl p-4 text-white"
      >
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 id={titleId} className="text-sm font-semibold tracking-tight">
            {isEdit ? 'Edit detail' : 'Add detail'}
          </h3>
          <button
            type="button"
            aria-label="Close"
            onClick={() => onCloseRef.current?.()}
            className="w-7 h-7 -mr-1 -mt-1 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10"
          >
            <X size={14} />
          </button>
        </div>
        <label className="block mb-3">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-wide text-white/55 mb-1">
            Key
          </span>
          <input
            ref={keyRef}
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="e.g. Fiber"
            autoComplete="off"
            spellCheck={false}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-400/40"
            style={{
              backgroundColor: '#ffffff',
              color: '#0f172a',
              caretColor: '#0f172a',
              WebkitTextFillColor: '#0f172a',
            }}
          />
        </label>
        <label className="block mb-4">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-wide text-white/55 mb-1">
            Value
          </span>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="e.g. Polyester"
            autoComplete="off"
            spellCheck={false}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-400/40"
            style={{
              backgroundColor: '#ffffff',
              color: '#0f172a',
              caretColor: '#0f172a',
              WebkitTextFillColor: '#0f172a',
            }}
          />
        </label>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => onCloseRef.current?.()}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white/75 hover:text-white hover:bg-white/10"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!String(label || '').trim() && !String(value || '').trim()}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-500 text-white hover:bg-sky-400 disabled:opacity-40 disabled:pointer-events-none"
          >
            {isEdit ? 'Save' : 'Add'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

/**
 * Outer frame for the fabric overlay — move + SE corner window resizer.
 */
function FabricSwatchFrame({
  children,
  editable = false,
  size = DEFAULT_FABRIC_SIZE,
  onSizeChange,
  widthPct,
  maxPx,
  pos,
  onPosChange,
  containerRef,
}) {
  const frameRef = useRef(null);
  const dragRef = useRef(null);
  const sizeRef = useRef(size);
  const onSizeChangeRef = useRef(onSizeChange);
  const [resizing, setResizing] = useState(false);
  const { dragging: moving, beginDrag } = useOverlayDrag({
    enabled: editable,
    pos,
    onPosChange,
    containerRef,
  });

  useEffect(() => {
    sizeRef.current = size;
  }, [size]);

  useEffect(() => {
    onSizeChangeRef.current = onSizeChange;
  }, [onSizeChange]);

  useEffect(() => {
    if (!resizing) return undefined;

    const onMove = (e) => {
      const drag = dragRef.current;
      const change = onSizeChangeRef.current;
      if (!drag || !change) return;
      const startW = Math.max(1, drag.startWidth);
      const deltaPx = (e.clientX - drag.startX + (e.clientY - drag.startY)) / 2;
      const nextSize = drag.origSize * ((startW + deltaPx) / startW);
      change(clamp(nextSize, MIN_FABRIC_SIZE, MAX_FABRIC_SIZE));
    };

    const onUp = () => {
      dragRef.current = null;
      setResizing(false);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [resizing]);

  const beginResize = (e) => {
    if (!editable || !onSizeChange || !frameRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = frameRef.current.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startWidth: rect.width,
      origSize: sizeRef.current,
    };
    setResizing(true);
  };

  const busy = resizing || moving;

  return (
    <div
      ref={frameRef}
      className={`absolute z-30 ${busy ? 'select-none' : ''}`}
      style={{
        left: `${pos.x}%`,
        top: `${pos.y}%`,
        width: `${widthPct}%`,
        maxWidth: maxPx,
      }}
    >
      <div
        className={`relative aspect-square rounded-xl overflow-hidden shadow-lg border border-white/70 bg-[#e8ddd4] ${
          busy ? 'ring-2 ring-sky-300/90' : ''
        }`}
      >
        {children}

        {editable && onPosChange && (
          <button
            type="button"
            data-resize-ui="true"
            data-export-hide="true"
            aria-label="Move fabric swatch"
            title="Drag to move swatch"
            onPointerDown={beginDrag}
            className="absolute top-1 left-1 z-50 w-7 h-7 rounded-md bg-white/95 text-[#1a2744] shadow border border-black/15 flex items-center justify-center cursor-grab active:cursor-grabbing touch-none"
          >
            <Move size={14} />
          </button>
        )}

        {editable && onSizeChange && (
          <CornerResizeHandle onPointerDown={beginResize} label="Resize fabric swatch box" />
        )}
      </div>
    </div>
  );
}

function KitCollage({ kitImages, editable, transforms, onTransformChange }) {
  const count = kitImages.length;
  if (count === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center text-[#5c564e]/70 text-xs px-4 text-center">
        Select kits to feature their designs here
      </div>
    );
  }

  if (count === 1) {
    return (
      <ImageTile
        src={kitImages[0]}
        alt="Design"
        label="design"
        className="absolute inset-0 h-full w-full"
        editable={editable}
        fit="cover"
        transform={transforms?.[0]}
        onTransformChange={(t) => onTransformChange?.(0, t)}
      />
    );
  }

  if (count === 2) {
    return (
      <div className="absolute inset-0 grid grid-cols-2 gap-1 p-1 bg-[#d9cfc4]">
        {kitImages.map((src, i) => (
          <ImageTile
            key={`${src}-${i}`}
            src={src}
            alt={`Design ${i + 1}`}
            label={`design ${i + 1}`}
            className="min-h-0 h-full rounded-sm"
            editable={editable}
            fit="cover"
            transform={transforms?.[i]}
            onTransformChange={(t) => onTransformChange?.(i, t)}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-1 p-1 bg-[#d9cfc4]">
      <ImageTile
        src={kitImages[0]}
        alt="Design 1"
        label="design 1"
        className="row-span-2 min-h-0 h-full rounded-sm"
        editable={editable}
        fit="cover"
        transform={transforms?.[0]}
        onTransformChange={(t) => onTransformChange?.(0, t)}
      />
      {kitImages.slice(1, 3).map((src, i) => (
        <ImageTile
          key={`${src}-${i + 1}`}
          src={src}
          alt={`Design ${i + 2}`}
          label={`design ${i + 2}`}
          className="min-h-0 h-full rounded-sm"
          editable={editable}
          fit="cover"
          transform={transforms?.[i + 1]}
          onTransformChange={(t) => onTransformChange?.(i + 1, t)}
        />
      ))}
    </div>
  );
}

export default function KitMarketingFlyer({
  copy,
  onCopyChange,
  editable = false,
  fabricImage,
  kitImages = [],
  imageTransforms,
  onImageTransformsChange,
  className = '',
}) {
  const stageRef = useRef(null);
  const detailsPanelRef = useRef(null);
  const detailsResizeRef = useRef(null);
  const detailsWidthRef = useRef(null);
  const setDetailsWidthPctRef = useRef(null);
  const [detailsResizing, setDetailsResizing] = useState(false);
  /** null = closed; { mode: 'add' } | { mode: 'edit', index } */
  const [detailModal, setDetailModal] = useState(null);
  const detailFont = 'system-ui, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif';
  const fontScale = nearestFontStep(Number(copy?.detailStyle?.fontScale) || 1);
  const detailColor = String(copy?.detailStyle?.color || '#ffffff').trim() || '#ffffff';
  const baseRem = 0.42 * fontScale;
  const isLightText = (() => {
    const hex = detailColor.replace('#', '');
    if (hex.length !== 6) return false;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 170;
  })();
  const textHalo = isLightText
    ? 'drop-shadow-[0_0_3px_rgba(0,0,0,0.85)]'
    : 'drop-shadow-[0_0_1.5px_rgba(255,255,255,0.55)]';
  const rowTextStyle = {
    fontSize: `${baseRem}rem`,
    color: detailColor,
    fontFamily: detailFont,
  };

  const detailRows = Array.isArray(copy.detailRows) ? copy.detailRows : [];

  const fabricPos = normalizePos(imageTransforms?.fabricPos, DEFAULT_FABRIC_POS);
  const detailsPos = normalizePos(imageTransforms?.detailsPos, DEFAULT_DETAILS_POS);
  const rawDetailsWidth = imageTransforms?.detailsWidthPct;
  const detailsWidthPct =
    rawDetailsWidth == null ||
    rawDetailsWidth === '' ||
    !Number.isFinite(Number(rawDetailsWidth))
      ? null
      : clamp(Number(rawDetailsWidth), MIN_DETAILS_WIDTH_PCT, MAX_DETAILS_WIDTH_PCT);

  const patchTransforms = (patch) => {
    onImageTransformsChange?.({
      fabric: imageTransforms?.fabric || { ...DEFAULT_TRANSFORM },
      fabricSize: imageTransforms?.fabricSize ?? DEFAULT_FABRIC_SIZE,
      fabricPos: normalizePos(imageTransforms?.fabricPos, DEFAULT_FABRIC_POS),
      detailsPos: normalizePos(imageTransforms?.detailsPos, DEFAULT_DETAILS_POS),
      detailsWidthPct:
        imageTransforms?.detailsWidthPct == null
          ? null
          : clamp(
              Number(imageTransforms.detailsWidthPct),
              MIN_DETAILS_WIDTH_PCT,
              MAX_DETAILS_WIDTH_PCT
            ),
      kits: imageTransforms?.kits || [],
      ...patch,
    });
  };

  const setField = (path, value) => {
    if (!onCopyChange) return;
    onCopyChange(patchKitMarketingCopy(copy, path, value));
  };

  const removeDetailRow = (index) => {
    if (!onCopyChange) return;
    onCopyChange({
      ...copy,
      detailRows: detailRows.filter((_, i) => i !== index),
    });
  };

  const saveDetailRow = ({ label, value }) => {
    if (!onCopyChange || !detailModal) return;
    const nextRow = {
      label: String(label || 'DETAIL').trim().toUpperCase() || 'DETAIL',
      value: String(value || '').trim(),
    };
    if (detailModal.mode === 'edit' && Number.isFinite(detailModal.index)) {
      const next = detailRows.map((row, i) =>
        i === detailModal.index ? nextRow : row
      );
      onCopyChange({ ...copy, detailRows: next });
      return;
    }
    onCopyChange({
      ...copy,
      detailRows: [...detailRows, nextRow],
    });
  };

  const setDetailStyle = (field, value) => {
    if (!onCopyChange) return;
    onCopyChange(
      patchKitMarketingCopy(copy, `detailStyle.${field}`, value)
    );
  };

  const setFabricTransform = (t) => {
    patchTransforms({ fabric: t });
  };

  const setFabricSize = (size) => {
    patchTransforms({ fabricSize: clamp(size, MIN_FABRIC_SIZE, MAX_FABRIC_SIZE) });
  };

  const setFabricPos = (pos) => {
    patchTransforms({ fabricPos: normalizePos(pos, DEFAULT_FABRIC_POS) });
  };

  const setDetailsPos = (pos) => {
    patchTransforms({ detailsPos: normalizePos(pos, DEFAULT_DETAILS_POS) });
  };

  const setDetailsWidthPct = (pct) => {
    patchTransforms({
      detailsWidthPct: clamp(pct, MIN_DETAILS_WIDTH_PCT, MAX_DETAILS_WIDTH_PCT),
    });
  };
  setDetailsWidthPctRef.current = setDetailsWidthPct;

  const setKitTransform = (index, t) => {
    const kits = [...(imageTransforms?.kits || [])];
    while (kits.length <= index) kits.push({ ...DEFAULT_TRANSFORM });
    kits[index] = t;
    patchTransforms({ kits });
  };

  const fabricSize = clamp(
    Number(imageTransforms?.fabricSize) || DEFAULT_FABRIC_SIZE,
    MIN_FABRIC_SIZE,
    MAX_FABRIC_SIZE
  );
  const fabricWidthPct = 28 * fabricSize;
  const fabricMaxPx = Math.round(140 * fabricSize);

  useEffect(() => {
    detailsWidthRef.current = detailsWidthPct;
  }, [detailsWidthPct]);

  useEffect(() => {
    if (!detailsResizing) return undefined;

    const onMove = (e) => {
      const drag = detailsResizeRef.current;
      const container = stageRef.current;
      const change = setDetailsWidthPctRef.current;
      if (!drag || !container || !change) return;
      const rect = container.getBoundingClientRect();
      if (rect.width < 1) return;
      const deltaPct = ((e.clientX - drag.startX) / rect.width) * 100;
      change(drag.origWidthPct + deltaPct);
    };

    const onUp = () => {
      detailsResizeRef.current = null;
      setDetailsResizing(false);
    };

    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
    document.addEventListener('pointercancel', onUp);
    return () => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      document.removeEventListener('pointercancel', onUp);
    };
  }, [detailsResizing]);

  const beginDetailsResize = (e) => {
    if (!editable || !detailsPanelRef.current || !stageRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    const panelRect = detailsPanelRef.current.getBoundingClientRect();
    const stageRect = stageRef.current.getBoundingClientRect();
    const measuredPct =
      stageRect.width > 0 ? (panelRect.width / stageRect.width) * 100 : MIN_DETAILS_WIDTH_PCT;
    const origWidthPct =
      detailsWidthRef.current == null
        ? clamp(measuredPct, MIN_DETAILS_WIDTH_PCT, MAX_DETAILS_WIDTH_PCT)
        : detailsWidthRef.current;
    // Lock measured width before drag so max-content → explicit %
    if (detailsWidthRef.current == null) {
      setDetailsWidthPct(origWidthPct);
    }
    detailsResizeRef.current = {
      startX: e.clientX,
      origWidthPct,
    };
    setDetailsResizing(true);
  };

  const {
    dragging: detailsMoving,
    beginDrag: beginDetailsDrag,
  } = useOverlayDrag({
    enabled: editable,
    pos: detailsPos,
    onPosChange: setDetailsPos,
    containerRef: stageRef,
  });

  const detailsBusy = detailsMoving || detailsResizing;

  return (
    <FlyerShell className={className}>
      <div ref={stageRef} className="relative flex-1 min-h-0 overflow-hidden bg-[#e8ddd4]">
        {/* Full-bleed kit — maximum space */}
        <KitCollage
          kitImages={kitImages}
          editable={editable}
          transforms={imageTransforms?.kits}
          onTransformChange={setKitTransform}
        />

        {/* Fabric swatch — movable + resizable */}
        <FabricSwatchFrame
          editable={editable}
          size={fabricSize}
          onSizeChange={setFabricSize}
          widthPct={fabricWidthPct}
          maxPx={fabricMaxPx}
          pos={fabricPos}
          onPosChange={setFabricPos}
          containerRef={stageRef}
        >
          <ImageTile
            src={fabricImage}
            alt="Fabric"
            label="fabric"
            className="absolute inset-0 h-full w-full"
            editable={editable}
            transform={imageTransforms?.fabric}
            onTransformChange={setFabricTransform}
            hideCornerHandle
          />
        </FabricSwatchFrame>

        {/* Fabric details — content-sized (or manually resized), never truncates */}
        <div
          ref={detailsPanelRef}
          className={`absolute z-20 rounded-lg backdrop-blur-md shadow-[0_6px_20px_rgba(0,0,0,0.35)] px-2.5 py-2 kit-marketing-details ${
            isLightText
              ? 'bg-slate-900/80 border border-white/30'
              : 'bg-white/72 border border-white/80'
          } ${editable ? 'pb-6 pr-1' : ''} ${detailsBusy ? 'ring-2 ring-sky-300/90 select-none' : ''}`}
          style={{
            left: `${detailsPos.x}%`,
            top: `${detailsPos.y}%`,
            width: detailsWidthPct != null ? `${detailsWidthPct}%` : 'max-content',
            minWidth: '7.5rem',
            maxWidth: `${MAX_DETAILS_WIDTH_PCT}%`,
            ['--kit-detail-color']: detailColor,
          }}
        >
          {editable && (
            <div
              data-export-hide="true"
              data-resize-ui="true"
              className="flex flex-wrap items-center gap-1 mb-1 pb-1 border-b border-white/15"
            >
              <div className="flex items-center gap-0.5">
                <button
                  type="button"
                  aria-label="Move details panel"
                  title="Drag to move details"
                  onPointerDown={beginDetailsDrag}
                  className="w-6 h-6 sm:w-5 sm:h-5 rounded flex items-center justify-center text-slate-700 bg-white/90 border border-slate-900/15 cursor-grab active:cursor-grabbing touch-none"
                >
                  <Move size={12} />
                </button>
                <button
                  type="button"
                  aria-label="Decrease text size"
                  title="Smaller text"
                  disabled={fontScale <= DETAIL_FONT_STEPS[0]}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetailStyle('fontScale', nudgeFontScale(fontScale, -1));
                  }}
                  className="w-6 h-6 sm:w-5 sm:h-5 rounded flex items-center justify-center text-[0.55rem] font-black text-slate-700 bg-white/90 border border-slate-900/15 disabled:opacity-35"
                >
                  A−
                </button>
                <button
                  type="button"
                  aria-label="Increase text size"
                  title="Larger text"
                  disabled={fontScale >= DETAIL_FONT_STEPS[DETAIL_FONT_STEPS.length - 1]}
                  onClick={(e) => {
                    e.stopPropagation();
                    setDetailStyle('fontScale', nudgeFontScale(fontScale, 1));
                  }}
                  className="w-6 h-6 sm:w-5 sm:h-5 rounded flex items-center justify-center text-[0.62rem] font-black text-slate-800 bg-white/90 border border-slate-900/15 disabled:opacity-35"
                >
                  A+
                </button>
              </div>
              <div className="flex items-center gap-0.5 ml-auto">
                {DETAIL_COLOR_PRESETS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-label={`Set text color ${c}`}
                    title={c}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDetailStyle('color', c);
                    }}
                    className={`w-3.5 h-3.5 rounded-full border shrink-0 ${
                      detailColor.toLowerCase() === c.toLowerCase()
                        ? 'ring-1 ring-offset-1 ring-white border-white'
                        : 'border-white/40'
                    }`}
                    style={{ background: c }}
                  />
                ))}
                <label
                  className="relative w-3.5 h-3.5 rounded-full overflow-hidden border border-white/40 cursor-pointer shrink-0"
                  title="Custom color"
                  style={{
                    background:
                      'conic-gradient(red, yellow, lime, aqua, blue, magenta, red)',
                  }}
                >
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(detailColor) ? detailColor : '#ffffff'}
                    aria-label="Custom text color"
                    onChange={(e) => setDetailStyle('color', e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer"
                  />
                </label>
              </div>
            </div>
          )}
          {detailRows.length > 0 ? (
            <ul className="space-y-0.5">
              {detailRows.map((row, i) => (
                <li key={`${row.label}-${i}`} className="flex items-start gap-0.5 group/row">
                  <button
                    type="button"
                    disabled={!editable}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!editable) return;
                      setDetailModal({ mode: 'edit', index: i });
                    }}
                    className={`flex-1 min-w-0 text-left leading-snug break-words rounded-sm ${
                      editable
                        ? 'cursor-pointer hover:bg-white/10 focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-300/80'
                        : 'cursor-default'
                    }`}
                    title={editable ? 'Edit detail' : undefined}
                  >
                    <span
                      className={`kit-detail-text font-black uppercase tracking-[0.04em] ${textHalo}`}
                      style={rowTextStyle}
                    >
                      {row.label}
                    </span>
                    <span
                      className={`kit-detail-text font-black ${textHalo}`}
                      style={rowTextStyle}
                    >
                      {' - '}
                    </span>
                    <span
                      className={`kit-detail-text font-extrabold uppercase tracking-[0.01em] break-words whitespace-normal ${textHalo}`}
                      style={rowTextStyle}
                    >
                      {row.value}
                    </span>
                  </button>
                  {editable && (
                    <button
                      type="button"
                      data-export-hide="true"
                      data-resize-ui="true"
                      aria-label={`Remove ${row.label || 'detail'} row`}
                      title="Remove"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeDetailRow(i);
                      }}
                      className="shrink-0 w-3.5 h-3.5 mt-0.5 rounded-full flex items-center justify-center text-slate-700/80 hover:text-white hover:bg-slate-800/80 transition opacity-80 group-hover/row:opacity-100"
                    >
                      <X size={9} strokeWidth={3} />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-slate-700 leading-snug font-bold" style={{ fontSize: `${baseRem}rem` }}>
              {editable ? 'Add fabric details' : 'No details'}
            </p>
          )}
          {editable && (
            <button
              type="button"
              data-export-hide="true"
              onClick={(e) => {
                e.stopPropagation();
                setDetailModal({ mode: 'add' });
              }}
              className="mt-1 self-start text-[0.36rem] font-bold uppercase tracking-wide text-[#6b2c3e] hover:text-[#4a1d2a]"
            >
              + Add detail
            </button>
          )}
          {editable && (
            <CornerResizeHandle
              onPointerDown={beginDetailsResize}
              label="Resize fabric details panel"
            />
          )}
        </div>
        {editable && detailModal && (
          <DetailRowModal
            open
            mode={detailModal.mode}
            initialLabel={
              detailModal.mode === 'edit'
                ? detailRows[detailModal.index]?.label || ''
                : ''
            }
            initialValue={
              detailModal.mode === 'edit'
                ? detailRows[detailModal.index]?.value || ''
                : ''
            }
            onClose={() => setDetailModal(null)}
            onSave={saveDetailRow}
          />
        )}
      </div>
    </FlyerShell>
  );
}

export {
  DEFAULT_TRANSFORM as KIT_MARKETING_DEFAULT_IMAGE_TRANSFORM,
  DEFAULT_FABRIC_SIZE as KIT_MARKETING_DEFAULT_FABRIC_SIZE,
  DEFAULT_FABRIC_POS as KIT_MARKETING_DEFAULT_FABRIC_POS,
  DEFAULT_DETAILS_POS as KIT_MARKETING_DEFAULT_DETAILS_POS,
};
